import { getDeviceContext } from '@lib/analytics'
import { isClientError } from '@lib/apiError'
import { nowUnix } from '@lib/datetime'
import { queryKeys } from '@lib/queryKeys'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { onBookingMembershipChange } from './bookingEvents'
import * as chatApi from './chatApi'
import { newClientId } from './clientId'
import { newOutbox, type Outbox } from './outbox'
import {
	confirmSend,
	discardSend,
	emptyStream,
	failSend,
	finishAddingIdea,
	finishReaction,
	myReaction,
	postMessageBody,
	react,
	replaceMessage,
	retrySend,
	sendPending,
	startAddingIdea,
	updatePeople,
	type ChatReactionChoice,
	type ChatStream,
	type OutgoingMessage,
} from './core'

export interface StreamActions {
	/** Shows the Message at once and sends it, uploading its photos first. */
	send: (message: OutgoingMessage) => void
	/** Sends a failed Message again, with the same client id so the server can't make it twice. */
	retry: (clientId: string) => void
	/** Gives up on a failed send. */
	discard: (clientId: string) => void
	/** Mine comes off, another becomes mine; it shows at once and goes back if the server refuses it. */
	toggleReaction: (reaction: ChatReactionChoice, messageId: number) => void
	/** The Message's link becomes a trip Idea, whose card the Message then shows. */
	addLinkAsIdea: (messageId: number) => void
}

/**
 * A stream of Messages and what I do to it, done with the server: sending (with
 * retries), reacting, Add as idea, and showing who's on a booking once I join or leave
 * it. The Chat and each Discussion work through one and page their own Messages into it
 * with `update`. Every change runs against the latest stream, in order, as iOS's
 * `ChatStreamActions` does on its main queue. The Chat passes its trip's `outbox`, so its
 * unconfirmed sends outlive the page; without one they last as long as the stream.
 */
export const useStreamActions = (tripId: string | undefined, sharedOutbox?: Outbox) => {
	const queryClient = useQueryClient()
	const [ownOutbox] = useState(newOutbox)
	const outbox = sharedOutbox ?? ownOutbox
	const [stream, setStream] = useState<ChatStream>(emptyStream)
	const streamRef = useRef(stream)
	const uploadedPhotoIds = outbox.uploadedPhotoIds

	const update = useCallback((change: (current: ChatStream) => ChatStream) => {
		streamRef.current = change(streamRef.current)
		setStream(streamRef.current)
	}, [])

	// The stream holds the sends while it's shown, and hands them back to the outbox after,
	// for a send still on its way to settle there.
	useEffect(() => outbox.attach(update, () => streamRef.current), [outbox, update])

	// Joining or leaving a booking posts no Message, so its cards are updated in place.
	useEffect(
		() => onBookingMembershipChange((tag, people) => update(current => updatePeople(current, people, tag))),
		[update],
	)

	const deliver = useCallback(
		async (clientId: string, message: OutgoingMessage) => {
			if (!tripId) return
			try {
				let photoIds = uploadedPhotoIds.get(clientId) ?? []
				if (message.photos.length > 0 && !uploadedPhotoIds.has(clientId)) {
					const { photos } = await chatApi.uploadPhotos(
						tripId,
						message.photos.map(photo => photo.file),
					)
					photoIds = photos.map(photo => photo.id)
					uploadedPhotoIds.set(clientId, photoIds)
				}
				const sent = await chatApi.postMessage(tripId, postMessageBody(clientId, message, photoIds, getDeviceContext()))
				uploadedPhotoIds.delete(clientId)
				// Picked photos live in the browser until they're sent; the stream shows the
				// server's copies from here on.
				outbox.changeSends(current => confirmSend(current, sent))
				message.photos.forEach(photo => URL.revokeObjectURL(photo.url))
			} catch (error) {
				// The server refused the send, perhaps over its photos: a retry uploads them
				// again rather than name the same ones.
				if (isClientError(error)) uploadedPhotoIds.delete(clientId)
				// Wherever the sends are by now: I may have left the Chat while it was on its way.
				outbox.changeSends(current => failSend(current, clientId))
			}
		},
		[outbox, tripId, uploadedPhotoIds],
	)

	const send = useCallback(
		(message: OutgoingMessage) => {
			const clientId = newClientId()
			update(current => sendPending(current, message, clientId, nowUnix()))
			void deliver(clientId, message)
		},
		[deliver, update],
	)

	const retry = useCallback(
		(clientId: string) => {
			const retried = retrySend(streamRef.current, clientId)
			if (!retried) return
			update(() => retried.stream)
			void deliver(clientId, retried.pending.message)
		},
		[deliver, update],
	)

	const discard = useCallback(
		(clientId: string) => {
			const pending = streamRef.current.pending.find(p => p.clientId === clientId)
			pending?.message.photos.forEach(photo => URL.revokeObjectURL(photo.url))
			uploadedPhotoIds.delete(clientId)
			update(current => discardSend(current, clientId))
		},
		[update, uploadedPhotoIds],
	)

	const toggleReaction = useCallback(
		(reaction: ChatReactionChoice, messageId: number) => {
			if (!tripId) return
			const wanted = myReaction(streamRef.current, messageId) === reaction ? null : reaction
			const reacted = react(streamRef.current, wanted, messageId)
			if (!reacted) return
			update(() => reacted.stream)
			chatApi
				.setReaction(tripId, messageId, wanted)
				.catch(() => null)
				.then(answer => {
					const finished = finishReaction(streamRef.current, reacted.change, answer)
					update(() => finished.stream)
					if (finished.failed) toast.error("Couldn't save your reaction. Please try again.")
				})
		},
		[tripId, update],
	)

	const addLinkAsIdea = useCallback(
		(messageId: number) => {
			if (!tripId || streamRef.current.addingIdeas.includes(messageId)) return
			update(current => startAddingIdea(current, messageId))
			chatApi.addLinkAsIdea(tripId, messageId).then(
				message => {
					update(current => replaceMessage(finishAddingIdea(current, messageId), message))
					void queryClient.invalidateQueries({ queryKey: queryKeys.ideas(tripId) })
				},
				() => {
					update(current => finishAddingIdea(current, messageId))
					toast.error("Couldn't add it as an idea. Please try again.")
				},
			)
		},
		[queryClient, tripId, update],
	)

	const actions = useMemo<StreamActions>(
		() => ({ send, retry, discard, toggleReaction, addLinkAsIdea }),
		[send, retry, discard, toggleReaction, addLinkAsIdea],
	)
	return { stream, streamRef, update, actions }
}
